import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlatformAdminView } from '../../api.js';
import { useAdminAppContext } from '../../context/AdminAppContext.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';

export function PlatformAdminsTab({ http, onToast }: { http: AdminHttpClient; onToast: (toast: ToastMessage) => void }) {
  const { t } = useTranslation();
  const { session } = useAdminAppContext();
  const [admins, setAdmins] = useState<PlatformAdminView[]>([]);
  const selfId = session.session?.bootstrap.user.id ?? '';
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setAdmins(await http.listPlatformAdmins());
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    }
  }, [http, onToast, t]);
  useEffect(() => {
    void load();
  }, [load]);
  // Designate by email: the service reuses an existing account (leaving its
  // login untouched) or provisions a new one with the given password.
  async function grant(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await http.grantPlatformAdmin({ email: email.trim(), name: name.trim(), password: password || undefined });
      setEmail('');
      setName('');
      setPassword('');
      await load();
      onToast({ tone: 'success', message: t('platformConsole.adminGranted') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    } finally {
      setBusy(false);
    }
  }
  async function revoke(id: string) {
    setBusy(true);
    try {
      await http.revokePlatformAdmin(id);
      await load();
      onToast({ tone: 'success', message: t('common.changed') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    } finally {
      setBusy(false);
    }
  }
  return <section className="content-stack">
    <section className="panel">
      <ul className="plain-list">
        {admins.map((admin) => <li key={admin.id}><span>{admin.name ? `${admin.name} · ${admin.email}` : admin.email}</span><button type="button" className="secondary" disabled={busy || admin.id === selfId} onClick={() => void revoke(admin.id)}>{t('platformConsole.revoke')}</button></li>)}
        {!admins.length && <li className="muted">{t('platformConsole.noAdmins')}</li>}
      </ul>
    </section>
    <form className="panel toolbar-form" onSubmit={grant}>
      <input placeholder={t('platformConsole.adminEmail')} value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" />
      <input placeholder={t('platformConsole.adminName')} value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" />
      <input type="password" placeholder={t('platformConsole.adminPasswordOptional')} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" />
      <button disabled={!email.trim() || busy}>{t('platformConsole.grantAdmin')}</button>
    </form>
  </section>;
}
